import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, Pager, PageSpinner } from '@/components/common';
import { useOUItems, useUpdateOU, useDeleteOU, useSetOuActive } from '@/hooks/useOUs';
import type { OUItem } from '@/api/types';
import {
  buildOuTree,
  collectIdsWithChildren,
  collectMatchIds,
  collectAncestorIdsOfMatches,
  rootsWithMatches,
  OuTreeRow,
} from '@/components/common/OuTree';

// IANA timezones relevant to Sigma operations
const TIMEZONE_OPTIONS = [
  { value: 'America/Mexico_City', label: 'América/México (Centro)' },
  { value: 'America/Monterrey',   label: 'América/Monterrey (Norte)' },
  { value: 'America/Bogota',      label: 'América/Bogotá (LATAM)' },
  { value: 'America/New_York',    label: 'América/Nueva York (EST)' },
  { value: 'America/Chicago',     label: 'América/Chicago (CST)' },
  { value: 'America/Los_Angeles', label: 'América/Los Ángeles (PST)' },
  { value: 'Europe/Madrid',       label: 'Europa/Madrid' },
  { value: 'Europe/London',       label: 'Europa/Londres' },
  { value: 'Asia/Shanghai',       label: 'Asia/Shanghái' },
  { value: 'UTC',                 label: 'UTC' },
];

interface OUFormState {
  open: boolean;
  editId: string | null;
  name: string;
  description: string;
  type: 'OU' | 'BU' | '';
  gdpr: boolean;
  timezone: string;
  errors: string[];
}

function makeDefault(): OUFormState {
  return { open: false, editId: null, name: '', description: '', type: '', gdpr: false, timezone: '', errors: [] };
}

const DEFAULT_PAGE_SIZE = 10;

export function OUsSubTab() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [manualCollapsed, setManualCollapsed] = useState<Set<string> | null>(null);
  const [form, setForm] = useState<OUFormState>(makeDefault);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const { data: ous = [], isLoading } = useOUItems({ activeOnly: false });
  const updateOU   = useUpdateOU();
  const deleteOU   = useDeleteOU();
  const setActive  = useSetOuActive();

  const tree = useMemo(() => buildOuTree(ous), [ous]);
  const idsWithChildren = useMemo(() => collectIdsWithChildren(tree), [tree]);
  const collapsedIds = manualCollapsed ?? idsWithChildren;

  const trimmedSearch = search.trim().toLowerCase();
  const matchIds = trimmedSearch ? collectMatchIds(tree, trimmedSearch) : null;
  const forcedOpenIds = matchIds ? collectAncestorIdsOfMatches(tree, matchIds) : null;

  function isCollapsed(id: string): boolean {
    if (forcedOpenIds?.has(id)) return false;
    return collapsedIds.has(id);
  }

  function toggleNode(id: string) {
    setManualCollapsed(prev => {
      const next = new Set(prev ?? idsWithChildren);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const visibleRoots = matchIds ? rootsWithMatches(tree, matchIds) : tree;
  const totalMatches = matchIds ? matchIds.size : ous.length;
  const pagedRoots = visibleRoots.slice((page - 1) * pageSize, page * pageSize);

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  function openEdit(ou: OUItem) {
    setForm({
      open: true,
      editId: ou.id,
      name: ou.name,
      description: ou.description ?? '',
      type: (ou.type as 'OU' | 'BU' | '') ?? '',
      gdpr: ou.gdpr ?? false,
      timezone: ou.timezone ?? '',
      errors: [],
    });
  }

  function validate(f: OUFormState): string[] {
    const errs: string[] = [];
    if (!f.name.trim()) errs.push(t('admin.ouErrorName'));
    if (!f.timezone) errs.push(t('admin.ouErrorTimezone'));
    return errs;
  }

  async function handleSubmit() {
    const errs = validate(form);
    if (errs.length) { setForm(f => ({ ...f, errors: errs })); return; }

    const body = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      type: form.type || undefined,
      gdpr: form.gdpr,
      timezone: form.timezone,
    };

    await updateOU.mutateAsync({ ouId: form.editId!, ...body });
    setForm(makeDefault());
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await deleteOU.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
  }

  const isSaving = updateOU.isPending;

  function renderOuCells(ou: OUItem) {
    return (
      <>
        <td className="ou-cell-desc">{ou.description ?? '—'}</td>
        <td>
          {ou.type ? (
            <span className={`ou-badge ou-badge--${ou.type.toLowerCase()}`}>{ou.type}</span>
          ) : '—'}
        </td>
        <td className="ou-cell-tz">
          {ou.timezone ? (
            <span className="ou-tz-label" title={ou.timezone}>
              <i className="fas fa-clock" aria-hidden="true" />
              {ou.timezone.replace('America/', '').replace('Europe/', '').replace('Asia/', '')}
            </span>
          ) : <span className="ou-tz-missing">—</span>}
        </td>
        <td>
          {ou.gdpr && (
            <span className="ou-badge ou-badge--gdpr">
              <i className="fas fa-shield-alt" /> GDPR
            </span>
          )}
        </td>
        <td>
          <button
            className={`ou-toggle${ou.isActive ? ' ou-toggle--on' : ''}`}
            onClick={() => setActive.mutate({ ouId: ou.id, isActive: !ou.isActive })}
            title={ou.isActive ? t('admin.ouDeactivate') : t('admin.ouActivate')}
          >
            <i className={`fas fa-${ou.isActive ? 'toggle-on' : 'toggle-off'}`} />
          </button>
        </td>
        <td className="ou-cell-actions">
          <button className="ou-btn-icon" onClick={() => openEdit(ou)} title={t('admin.editOU')}>
            <i className="fas fa-pen" />
          </button>
          <button
            className="ou-btn-icon ou-btn-icon--danger"
            onClick={() => setDeleteTarget({ id: ou.id, name: ou.name })}
            title={t('admin.deleteOU')}
          >
            <i className="fas fa-trash" />
          </button>
        </td>
      </>
    );
  }

  if (isLoading) return <PageSpinner />;

  return (
    <div className="ou-tab">
      {/* Header */}
      <div className="ou-tab-header">
        <div>
          <div className="ou-tab-title">
            <i className="fas fa-sitemap" aria-hidden="true" />
            {t('admin.ousTitle')}
          </div>
          <div className="ou-tab-subtitle">{t('admin.ousSubtitle')}</div>
        </div>
      </div>

      {/* Search */}
      <div className="ou-search-row">
        <div className="ou-search-wrap">
          <i className="fas fa-search ou-search-icon" aria-hidden="true" />
          <input
            className="ou-search-input"
            placeholder={t('admin.ousSearchPlaceholder')}
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
          />
        </div>
        <span className="ou-count">{totalMatches} OUs</span>
      </div>

      {/* Table */}
      {visibleRoots.length === 0 ? (
        <div className="ou-empty">{t('admin.ousNoResults')}</div>
      ) : (
        <>
          <div className="ou-table-wrap">
            <table className="ou-table">
              <thead>
                <tr>
                  <th>{t('admin.colName')}</th>
                  <th>{t('admin.ouDescription')}</th>
                  <th>{t('admin.ouType')}</th>
                  <th>{t('admin.ouTimezone')}</th>
                  <th>{t('admin.ouGdpr')}</th>
                  <th>{t('admin.ouActive')}</th>
                  <th>{t('admin.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {pagedRoots.map(node => (
                  <OuTreeRow
                    key={node.ou.id}
                    node={node}
                    depth={0}
                    isCollapsed={isCollapsed}
                    onToggle={toggleNode}
                    renderCells={renderOuCells}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            total={visibleRoots.length}
            page={page}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={s => { setPageSize(s); setPage(1); }}
          />
        </>
      )}

      {/* Form modal */}
      {form.open && (
        <div className="ou-modal-overlay" onClick={() => setForm(makeDefault())}>
          <div className="ou-modal" onClick={e => e.stopPropagation()}>
            <div className="ou-modal-header">
              <span>{t('admin.editOU')}</span>
              <button className="ou-modal-close" onClick={() => setForm(makeDefault())}>
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="ou-modal-body">
              {form.errors.length > 0 && (
                <div className="ou-form-errors">
                  {form.errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}

              <label className="ou-form-label">
                {t('admin.colName')} *
                <input
                  className="ou-form-input"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </label>

              <label className="ou-form-label">
                {t('admin.ouDescription')}
                <input
                  className="ou-form-input"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </label>

              <label className="ou-form-label">
                {t('admin.ouType')}
                <select
                  className="ou-form-input"
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value as 'OU' | 'BU' | '' }))}
                >
                  <option value="">—</option>
                  <option value="OU">OU</option>
                  <option value="BU">BU</option>
                </select>
              </label>

              <label className="ou-form-label">
                {t('admin.ouTimezone')} *
                <select
                  className="ou-form-input"
                  value={form.timezone}
                  onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}
                >
                  <option value="">— {t('admin.ouTimezoneSelect')} —</option>
                  {TIMEZONE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>

              <label className="ou-form-label ou-form-label--checkbox">
                <input
                  type="checkbox"
                  checked={form.gdpr}
                  onChange={e => setForm(f => ({ ...f, gdpr: e.target.checked }))}
                />
                {t('admin.ouGdpr')}
              </label>
            </div>

            <div className="ou-modal-footer">
              <button className="ou-btn-cancel" onClick={() => setForm(makeDefault())}>
                {t('common.cancel')}
              </button>
              <button className="ou-btn-save" onClick={handleSubmit} disabled={isSaving}>
                {isSaving ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <ConfirmModal
          title={t('admin.deleteOU')}
          message={t('admin.deleteOUConfirm', { name: deleteTarget.name })}
          confirmText={t('common.delete')}
          type="danger"
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
